import React, {ReactNode} from 'react'

import BrandLogo from '../../../designSystem/BrandLogo'
import urls from '@daedalus/core/src/utils/url'
import Avatar, {AvatarType} from '../../../designSystem/Avatar'
import Button from '../../../designSystem/Button'
import {
  AvatarWrapper,
  BackButtonElement,
  BackIcon,
  ContentWrapper,
  HamburgerElement,
  HamburgerIcon,
  HeaderElement,
  LogoElement,
  LogoLinkElement,
  LogoAnchorElement,
  Spacer
} from './styles'

import {BrandType} from '@daedalus/core/src/brand/types'
import {ClaimsType} from '@daedalus/core/src/auth/types/Auth'
import {getUserName} from '@daedalus/core/src/user/business'
import {
  PlusIcon,
  PlusIconWrapper
} from '../../privateDeals/PrivateDealTag/PlusIcon'

type User = ClaimsType | null | undefined
type OnBackButtonClick = (event: React.SyntheticEvent) => void

interface Props {
  /** Brand configuration. It can be FindHotel or Etrip */
  brand: BrandType
  /** Content to render in the middle of the header */
  children?: ReactNode
  /** Whether the logo icon should be disabled */
  disableLogoClick?: boolean
  /** Whether to show user Avatar */
  shouldShowUserAvatar?: boolean
  /** Callback for BackButton click */
  onBackButtonClick?: OnBackButtonClick
  /** Callback for ShowPreferencesButton click */
  onShowPreferences?: () => void
  /** Whether to show the BackButton */
  showBackButton?: boolean
  /** Whether to show the a center positioned logo */
  showCenterLogo?: boolean
  /** Whether to show the content. This will be ignored if showCenterLogo equals true */
  showContent?: boolean
  /** Whether to show the ShowPreferencesButton */
  showSettings?: boolean
  /** User object with metadata such as profile picture, name etc. */
  user?: User
  /** Custom content to be displayed on the right with priority */
  rightContent?: typeof React.Component
  /** Whether to display a bottom shadow */
  hasShadow?: boolean
  /** Which url should the logo send the user when clicked upon */
  logoUrl?: string
  /** Whether user is Plus user */
  isMemberPlus?: boolean
}

interface LogoProps {
  disableLogoClick?: boolean
  brand: BrandType
  logoUrl?: string
}

interface DisplayLogoElementProps extends LogoProps {
  compact?: boolean
}

interface BackButtonProps {
  onBackButtonClick?: OnBackButtonClick
}

interface LeftIconProps {
  showBackButton?: boolean
  onBackButtonClick?: OnBackButtonClick
  showContent?: boolean
  showCenterLogo?: boolean
  disableLogoClick?: boolean
  brand: BrandType
  logoUrl?: string
}

interface AvatarProps {
  user: User
  isMemberPlus?: boolean
}

interface RightIconProps {
  showSettings?: boolean
  onShowPreferences?: () => void
  shouldShowUserAvatar: boolean
  user?: User
  isMemberPlus?: boolean
}

interface CenterContentProps {
  showCenterLogo?: boolean
  showContent?: boolean
  brand: BrandType
  disableLogoClick?: boolean
  children?: ReactNode
  logoUrl?: string
}

const DisplayLogoElement = ({
  compact,
  disableLogoClick,
  brand,
  logoUrl
}: DisplayLogoElementProps) => {
  if (disableLogoClick) {
    return (
      <LogoElement compact={compact} brand={brand.code}>
        <BrandLogo compact={compact} brand={brand} />
      </LogoElement>
    )
  }

  if (logoUrl) {
    return (
      <LogoAnchorElement compact={compact} brand={brand.code} href={logoUrl}>
        <BrandLogo compact={compact} brand={brand} />
      </LogoAnchorElement>
    )
  }

  return (
    <LogoLinkElement compact={compact} brand={brand.code} to={urls.root}>
      <BrandLogo compact={compact} brand={brand} />
    </LogoLinkElement>
  )
}

// NOTE: It is important to keep FullLogo and CompactLogo as separate components, so that
// the logo changes smoothly when you click on the search box
// https://github.com/FindHotel/daedalus/pull/3253#discussion_r776666082
const FullLogo = ({disableLogoClick, brand, logoUrl}: LogoProps) => {
  return (
    <DisplayLogoElement
      disableLogoClick={disableLogoClick}
      brand={brand}
      logoUrl={logoUrl}
    />
  )
}

const CompactLogo = ({disableLogoClick, brand, logoUrl}: LogoProps) => {
  return (
    <DisplayLogoElement
      compact
      disableLogoClick={disableLogoClick}
      brand={brand}
      logoUrl={logoUrl}
    />
  )
}

const BackButton = ({onBackButtonClick}: BackButtonProps) => (
  <BackButtonElement appearance="noStyle" onClick={onBackButtonClick}>
    <BackIcon size={24} />
  </BackButtonElement>
)

const LeftIcon = ({
  showBackButton,
  onBackButtonClick,
  showContent,
  showCenterLogo,
  disableLogoClick,
  brand,
  logoUrl
}: LeftIconProps) => {
  if (showBackButton && onBackButtonClick) {
    return <BackButton onBackButtonClick={onBackButtonClick} />
  }

  if (showContent && !showCenterLogo) {
    return (
      <CompactLogo
        disableLogoClick={disableLogoClick}
        brand={brand}
        logoUrl={logoUrl}
      />
    )
  }

  if (!showCenterLogo) {
    return (
      <FullLogo
        disableLogoClick={disableLogoClick}
        brand={brand}
        logoUrl={logoUrl}
      />
    )
  }

  return <Spacer />
}

const AvatarElement = ({user, isMemberPlus}: AvatarProps) => (
  <AvatarWrapper isMemberPlus={isMemberPlus}>
    <Avatar
      picture={user?.picture}
      name={getUserName(user)}
      type={user?.picture ? AvatarType.Picture : AvatarType.Icon}
    />
    {isMemberPlus && (
      <PlusIconWrapper>
        <PlusIcon variant="text" size="xs" translateY="0" />
      </PlusIconWrapper>
    )}
  </AvatarWrapper>
)

const Hamburger = () => (
  <HamburgerElement>
    <HamburgerIcon size={22} />
  </HamburgerElement>
)

const RightIcon = ({
  showSettings,
  onShowPreferences,
  shouldShowUserAvatar,
  user,
  isMemberPlus
}: RightIconProps) => {
  if (!showSettings) return <Spacer />

  return (
    <Button
      dataId="ShowPreferencesButton"
      appearance="noStyle"
      onClick={onShowPreferences}
    >
      {shouldShowUserAvatar && user ? (
        <AvatarElement user={user} isMemberPlus={isMemberPlus} />
      ) : (
        <Hamburger />
      )}
    </Button>
  )
}

const CenterContent = ({
  showCenterLogo,
  showContent,
  brand,
  disableLogoClick,
  children,
  logoUrl
}: CenterContentProps) => (
  <>
    {showCenterLogo && (
      <FullLogo
        brand={brand}
        logoUrl={logoUrl}
        disableLogoClick={disableLogoClick}
      />
    )}

    {!showCenterLogo && showContent && (
      <ContentWrapper>{children}</ContentWrapper>
    )}
  </>
)

const CompactHeader = ({
  brand,
  children,
  disableLogoClick = false,
  shouldShowUserAvatar = false,
  onBackButtonClick,
  onShowPreferences,
  showBackButton = false,
  showContent = true,
  showSettings = false,
  showCenterLogo = false,
  user,
  rightContent: RightContent,
  hasShadow = false,
  logoUrl,
  isMemberPlus
}: Props) => (
  <HeaderElement hasShadow={hasShadow}>
    <LeftIcon
      showBackButton={showBackButton}
      showContent={showContent}
      showCenterLogo={showCenterLogo}
      disableLogoClick={disableLogoClick}
      brand={brand}
      logoUrl={logoUrl}
      onBackButtonClick={onBackButtonClick}
    />

    <CenterContent
      showCenterLogo={showCenterLogo}
      showContent={showContent}
      brand={brand}
      logoUrl={logoUrl}
      disableLogoClick={disableLogoClick}
    >
      {children}
    </CenterContent>

    {RightContent ? (
      <RightContent />
    ) : (
      <RightIcon
        showSettings={showSettings}
        shouldShowUserAvatar={shouldShowUserAvatar}
        user={user}
        isMemberPlus={isMemberPlus}
        onShowPreferences={onShowPreferences}
      />
    )}
  </HeaderElement>
)

export default CompactHeader
